'use client';

import { useContext, useEffect, useRef } from 'react';

import { type NavigationGuard, NavigationGuardContext } from './context';

/**
 * Register a navigation guard for the lifetime of the calling component.
 *
 * The guard is registered once per mount; the latest callbacks are read through
 * a ref on every invocation, so callers can pass inline closures without
 * causing effect churn or re-registration on each render.
 */
export function useRegisterNavigationGuard(guard: NavigationGuard): void {
  const context = useContext(NavigationGuardContext);
  const guardRef = useRef(guard);
  guardRef.current = guard;

  useEffect(() => {
    if (!context) {
      return;
    }
    return context.registerGuard({
      shouldBlock: () => guardRef.current.shouldBlock(),
      onBlocked: (destination) => guardRef.current.onBlocked(destination),
    });
  }, [context]);
}

const neverIntercept = () => false;

/**
 * For navigation links: returns an interceptor to consult inside next/link's
 * onNavigate. When it returns true, the caller must event.preventDefault().
 *
 * Null-safe: a stable no-op is returned when no provider is mounted, so
 * components using this stay renderable outside the guarded layout.
 */
export function useNavigationInterceptor(): (href: string) => boolean {
  const context = useContext(NavigationGuardContext);
  return context?.interceptNavigation ?? neverIntercept;
}
